import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  FileDown,
  Calendar,
  CheckCircle2,
  Clock,
  Printer,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Sparkles,
  FileText,
  MessageCircle,
} from "lucide-react";
import { apiGet, openPdfPreview, getFileBlob } from "../../shared/apiClient";
import { useToast } from "../../hooks/useToast";
import { CollapsibleSection } from "../CollapsibleSection";

interface ReportGiornalieroItem {
  chiamataId: string;
  chiamataDescrizione: string;
  impiantoCodice?: string;
  impiantoNome?: string;
  statoChiamata: string;
  tipo: 'chiusa' | 'da_terminare';
  reportId?: string | null;
  sequenza: number;
  esito?: string;
  orario?: string;
}

interface ReportGiornalieriResponse {
  data: string;
  totale: number;
  totaleChiuse: number;
  totaleDaTerminare: number;
  items: ReportGiornalieroItem[];
}

function getTodayRomeStr(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Rome' });
}

function shiftDate(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

function formatDateDisplay(dateStr: string): string {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('it-IT', {
    timeZone: 'UTC',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function ReportGiornalieriSection() {
  const [dataSelezionata, setDataSelezionata] = useState<string>(getTodayRomeStr());
  const [downloadingAll, setDownloadingAll] = useState<boolean>(false);
  const { toast } = useToast();

  const isToday = dataSelezionata === getTodayRomeStr();

  const { data: resData, isLoading, isError, refetch } = useQuery<ReportGiornalieriResponse>({
    queryKey: ['report-giornalieri', dataSelezionata],
    queryFn: async () => {
      const res = await apiGet<ReportGiornalieriResponse>(`/chiamate/report-giornalieri?data=${dataSelezionata}`);
      return res.data;
    },
  });

  const items = resData?.items || [];
  const totale = resData?.totale || 0;
  const totaleChiuse = resData?.totaleChiuse || 0;
  const totaleDaTerminare = resData?.totaleDaTerminare || 0;

  const handleDownloadAllPdf = async () => {
    if (totale === 0) {
      toast({ type: 'info', message: 'Nessun report da scaricare per la data selezionata' });
      return;
    }
    setDownloadingAll(true);
    try {
      await openPdfPreview(`/chiamate/report-giornalieri/pdf?data=${dataSelezionata}`);
    } catch {
      toast({ type: 'error', message: 'Impossibile generare il PDF giornaliero' });
    } finally {
      setDownloadingAll(false);
    }
  };

  const handleDownloadSinglePdf = async (item: ReportGiornalieroItem) => {
    try {
      if (item.reportId) {
        await openPdfPreview(`/chiamate/${item.chiamataId}/report/${item.reportId}/pdf`);
      } else {
        await openPdfPreview(`/chiamate/${item.chiamataId}/pdf`);
      }
    } catch {
      toast({ type: 'error', message: 'Impossibile generare il PDF del report' });
    }
  };

  const handleShareWhatsappPdf = async (item?: ReportGiornalieroItem) => {
    try {
      const endpoint = item
        ? (item.reportId ? `/chiamate/${item.chiamataId}/report/${item.reportId}/pdf` : `/chiamate/${item.chiamataId}/pdf`)
        : `/chiamate/report-giornalieri/pdf?data=${dataSelezionata}`;

      const defaultFilename = item
        ? `report_${item.impiantoCodice || 'intervento'}_${dataSelezionata}.pdf`
        : `report_giornalieri_${dataSelezionata}.pdf`;

      // 1. Tenta la condivisione diretta del FILE .pdf via Web Share API (Android/iOS PWA)
      if (typeof navigator !== 'undefined' && navigator.share && navigator.canShare) {
        try {
          const { blob, filename } = await getFileBlob(endpoint);
          const pdfFile = new File([blob], filename || defaultFilename, { type: 'application/pdf' });

          if (navigator.canShare({ files: [pdfFile] })) {
            await navigator.share({
              title: item ? `Report Intervento Imp. ${item.impiantoCodice}` : `Report Giornalieri ${dataSelezionata}`,
              text: item
                ? `📄 Report Intervento Semaforico - Imp. ${item.impiantoCodice} (${item.impiantoNome || ''})`
                : `📄 Report Interventi Giornalieri del ${dataSelezionata}`,
              files: [pdfFile],
            });
            toast({ type: 'success', message: 'Condivisione inviata' });
            return;
          }
        } catch (e: any) {
          if (e.name === 'AbortError') return; // Utente ha annullato la condivisione
        }
      }

      // 2. Fallback su apertura link WhatsApp con messaggio precompilato
      const textMessage = item
        ? `📄 *Report Intervento Semaforico*\n` +
          `🏢 Impianto: Imp. ${item.impiantoCodice} ${item.impiantoNome ? `(${item.impiantoNome})` : ''}\n` +
          `📌 Stato: ${item.tipo === 'chiusa' ? 'Chiusa (Risolta)' : 'Da terminare'}\n` +
          `📅 Data: ${formatDateDisplay(dataSelezionata)}\n` +
          `📝 Note: ${item.chiamataDescrizione || 'Intervento effettuato'}`
        : `📄 *Report Giornalieri Interventi Semaforici*\n` +
          `📅 Data: ${formatDateDisplay(dataSelezionata)}\n` +
          `📊 Totale Interventi: ${totale} (${totaleChiuse} chiusi, ${totaleDaTerminare} da terminare)`;

      const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(textMessage)}`;
      window.open(whatsappUrl, '_blank');
    } catch {
      toast({ type: 'error', message: 'Impossibile preparare la condivisione WhatsApp' });
    }
  };

  return (
      <CollapsibleSection
        icon={FileDown}
        iconClass="bg-emerald-500 text-white"
        title="Report Giornalieri PDF"
        subtitle="Scarica o visualizza i report degli interventi chiusi e da terminare"
        defaultOpen={false}
      >
        {/* Barra Selezione Data */}
        <div className="pt-2 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition active:scale-95"
              onClick={() => setDataSelezionata((prev) => shiftDate(prev, -1))}
              title="Giorno precedente"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <div className="relative flex items-center">
              <input
                type="date"
                value={dataSelezionata}
                onChange={(e) => e.target.value && setDataSelezionata(e.target.value)}
                className="pl-9 pr-3 py-1.5 text-xs sm:text-sm font-bold text-slate-800 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:outline-hidden"
              />
              <Calendar className="w-4 h-4 text-slate-400 absolute left-3 pointer-events-none" />
            </div>

            <button
              type="button"
              className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition active:scale-95"
              onClick={() => setDataSelezionata((prev) => shiftDate(prev, 1))}
              title="Giorno successivo"
            >
              <ChevronRight className="w-4 h-4" />
            </button>

            {!isToday && (
              <button
                type="button"
                className="text-xs font-bold px-2.5 py-1.5 rounded-xl bg-emerald-100 text-emerald-800 hover:bg-emerald-200 transition"
                onClick={() => setDataSelezionata(getTodayRomeStr())}
              >
                Oggi
              </button>
            )}
          </div>

          <span className="text-xs font-semibold text-slate-600 capitalize">
            {formatDateDisplay(dataSelezionata)}
          </span>
        </div>

        {/* Contatori di sintesi */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          <div className="bg-white rounded-2xl p-3 sm:p-4 border border-slate-200 shadow-2xs text-center">
            <div className="text-xs text-slate-500 font-bold mb-0.5">Totale Report</div>
            <div className="text-xl sm:text-2xl font-black text-slate-900">{isLoading ? '...' : totale}</div>
          </div>

          <div className="bg-white rounded-2xl p-3 sm:p-4 border border-emerald-200 bg-emerald-50/30 shadow-2xs text-center">
            <div className="text-xs text-emerald-700 font-bold mb-0.5 flex items-center justify-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> Chiusi
            </div>
            <div className="text-xl sm:text-2xl font-black text-emerald-700">{isLoading ? '...' : totaleChiuse}</div>
          </div>

          <div className="bg-white rounded-2xl p-3 sm:p-4 border border-amber-200 bg-amber-50/30 shadow-2xs text-center">
            <div className="text-xs text-amber-700 font-bold mb-0.5 flex items-center justify-center gap-1">
              <Clock className="w-3.5 h-3.5" /> Da Terminare
            </div>
            <div className="text-xl sm:text-2xl font-black text-amber-700">{isLoading ? '...' : totaleDaTerminare}</div>
          </div>
        </div>

        {/* Pulsante Principale di Download Cumulativo */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-700 rounded-3xl p-4 sm:p-5 text-white shadow-md flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="space-y-1 text-center sm:text-left">
            <div className="flex items-center justify-center sm:justify-start gap-2 font-black text-base sm:text-lg">
              <Sparkles className="w-5 h-5 text-emerald-200" />
              Scarica Documento Unico PDF
            </div>
            <p className="text-xs text-emerald-100 leading-relaxed max-w-md">
              Genera un unico file PDF multi-pagina vettoriale (Modello 8.5.1 SIC) con tutti i report degli interventi del giorno pronti per la stampa.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-2 w-full sm:w-auto shrink-0">
            <button
              type="button"
              disabled={totale === 0 || downloadingAll || isLoading}
              onClick={handleDownloadAllPdf}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-emerald-800 font-extrabold px-4 py-2.5 rounded-2xl hover:bg-emerald-50 active:scale-95 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-xs shrink-0 text-xs sm:text-sm"
            >
              {downloadingAll ? (
                <>
                  <div className="w-4 h-4 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
                  <span>Generazione...</span>
                </>
              ) : (
                <>
                  <FileDown className="w-4 h-4" />
                  <span>Scarica Tutti ({totale})</span>
                </>
              )}
            </button>

            <button
              type="button"
              disabled={totale === 0 || downloadingAll || isLoading}
              onClick={() => handleShareWhatsappPdf()}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-extrabold px-4 py-2.5 rounded-2xl active:scale-95 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-xs shrink-0 text-xs sm:text-sm border border-emerald-400/40"
              title="Condividi tutti i report del giorno su WhatsApp"
            >
              <MessageCircle className="w-4 h-4" />
              <span>Condividi su WhatsApp</span>
            </button>
          </div>
        </div>

        {/* Elenco dettagliato interventi del giorno */}
        <div className="space-y-2">
          <div className="flex items-center justify-between px-1">
            <h3 className="text-xs sm:text-sm font-bold text-slate-700 uppercase tracking-wider">
              Elenco Interventi ({totale})
            </h3>
            <button
              type="button"
              className="text-xs text-emerald-600 hover:text-emerald-700 font-bold"
              onClick={() => refetch()}
            >
              Aggiorna
            </button>
          </div>

          {isLoading ? (
            <div className="bg-white rounded-2xl p-8 border border-slate-200 text-center text-slate-400 text-sm">
              Caricamento interventi in corso...
            </div>
          ) : isError ? (
            <div className="bg-red-50 rounded-2xl p-6 border border-red-200 text-center text-red-700 text-sm flex items-center justify-center gap-2">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <span>Errore durante il caricamento dei report giornalieri</span>
            </div>
          ) : items.length === 0 ? (
            <div className="bg-white rounded-3xl p-8 border border-slate-200 text-center space-y-2">
              <FileText className="w-10 h-10 text-slate-300 mx-auto" />
              <div className="font-bold text-slate-700 text-sm">Nessun intervento registrato</div>
              <p className="text-xs text-slate-400 max-w-sm mx-auto">
                Per questa data non sono presenti chiamate chiuse o messe in stato da terminare da questa squadra.
              </p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {items.map((item, idx) => {
                const isChiusa = item.tipo === 'chiusa';
                return (
                  <div
                    key={`${item.chiamataId}-${item.reportId || idx}`}
                    className="bg-white rounded-2xl p-4 border border-slate-200 shadow-2xs hover:border-slate-300 transition flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                  >
                    <div className="space-y-1.5 flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-extrabold text-sm text-slate-900">
                          {item.impiantoCodice ? `Imp. ${item.impiantoCodice}` : 'Impianto'}
                        </span>
                        {item.impiantoNome && (
                          <span className="text-xs text-slate-500 truncate max-w-xs">
                            {item.impiantoNome}
                          </span>
                        )}
                        <span
                          className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${
                            isChiusa
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {isChiusa ? 'Chiusa (Risolta)' : 'Da terminare (Parziale)'}
                        </span>
                      </div>

                      <p className="text-xs text-slate-600 line-clamp-2">
                        {item.chiamataDescrizione || 'Nessuna descrizione specificata'}
                      </p>

                      {item.orario && (
                        <div className="text-[11px] text-slate-400 font-medium">
                          Orario: {new Date(item.orario).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => handleDownloadSinglePdf(item)}
                        className="inline-flex items-center justify-center gap-1.5 text-xs font-bold px-3 py-2 rounded-xl bg-slate-100 hover:bg-emerald-50 hover:text-emerald-700 text-slate-700 transition active:scale-95 shrink-0 border border-slate-200"
                        title="Scarica singolo report in PDF"
                      >
                        <Printer className="w-3.5 h-3.5" />
                        <span>PDF</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => handleShareWhatsappPdf(item)}
                        className="inline-flex items-center justify-center gap-1.5 text-xs font-bold px-3 py-2 rounded-xl bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200 transition active:scale-95 shrink-0"
                        title="Condividi report su WhatsApp"
                      >
                        <MessageCircle className="w-3.5 h-3.5 text-emerald-600" />
                        <span>WhatsApp</span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CollapsibleSection>
  );
}
